import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, ArrowLeft, CheckCircle2, HelpCircle } from "lucide-react";

const TONE_STYLES = {
  amber:   { bg: "bg-amber-100",   text: "text-amber-700",   ring: "ring-amber-200",   header: "from-amber-50 to-orange-50",  icon: "from-amber-500 to-orange-500"  },
  indigo:  { bg: "bg-indigo-100",  text: "text-indigo-700",  ring: "ring-indigo-200",  header: "from-indigo-50 to-violet-50", icon: "from-indigo-500 to-violet-500" },
  violet:  { bg: "bg-violet-100",  text: "text-violet-700",  ring: "ring-violet-200",  header: "from-violet-50 to-fuchsia-50",icon: "from-violet-500 to-fuchsia-500"},
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-200", header: "from-emerald-50 to-teal-50",  icon: "from-emerald-500 to-teal-500"  },
  fuchsia: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", ring: "ring-fuchsia-200", header: "from-fuchsia-50 to-pink-50",  icon: "from-fuchsia-500 to-pink-500"  },
  rose:    { bg: "bg-rose-100",    text: "text-rose-700",    ring: "ring-rose-200",    header: "from-rose-50 to-pink-50",     icon: "from-rose-500 to-pink-500"     },
};

/**
 * Reusable first-visit walkthrough + persistent "How it works" help pill.
 * Renders both modal and side drawer via createPortal so ancestor transforms
 * (e.g. .stagger-in) don't clip them.
 *
 * Props:
 *   storageKey — localStorage key for the seen-flag (e.g. "bf_estimator_tour_v1")
 *   eyebrow    — small caps label above the title (e.g. "WELCOME")
 *   title      — modal + panel headline
 *   Icon       — lucide icon component for the header tile
 *   tone       — palette key (amber|indigo|violet|emerald|fuchsia|rose)
 *   steps      — array of {Icon, tone, title, body}
 */
export default function PageTour({ storageKey, eyebrow, title, Icon, tone = "violet", steps, suppressAutoOpen = false }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (suppressAutoOpen) return;
    if (storageKey && !localStorage.getItem(storageKey)) setModalOpen(true);
  }, [storageKey, suppressAutoOpen]);

  const closeModal = () => {
    if (storageKey) localStorage.setItem(storageKey, "seen");
    setModalOpen(false);
    setStep(0);
  };
  const reopen = () => { setStep(0); setPanelOpen(true); };

  const headerTone = TONE_STYLES[tone] || TONE_STYLES.violet;
  const s = steps[step] || steps[0];
  const stepTone = TONE_STYLES[s.tone] || headerTone;
  const StepIcon = s.Icon;
  const testidRoot = storageKey ? storageKey.replace(/^bf_/, "").replace(/_v\d+$/, "") : "tour";

  return (
    <>
      <button
        onClick={reopen}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono font-bold tracking-widest ${headerTone.text} ${headerTone.bg} hover:brightness-95 border border-current/10 transition-all`}
        data-testid={`${testidRoot}-help-btn`}
      >
        <HelpCircle className="w-3.5 h-3.5" />
        HOW IT WORKS
      </button>

      {modalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm" data-testid={`${testidRoot}-tour-modal`}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className={`px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r ${headerTone.header}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${headerTone.icon} text-white flex items-center justify-center`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className={`text-[10px] font-mono font-bold tracking-widest ${headerTone.text}`}>{eyebrow}</div>
                  <div className="font-display font-bold text-lg text-slate-900">{title}</div>
                </div>
              </div>
              <button onClick={closeModal} className="w-8 h-8 rounded-lg hover:bg-white/70 flex items-center justify-center text-slate-500 hover:text-rose-600" data-testid={`${testidRoot}-tour-close`}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${stepTone.bg} ${stepTone.text} text-[10px] font-mono font-bold tracking-widest mb-3`}>
                STEP {step + 1} OF {steps.length}
              </div>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${stepTone.bg} ${stepTone.text} flex items-center justify-center shrink-0`}>
                  <StepIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-8">
                <div className="flex gap-1.5">
                  {steps.map((_, i) => (
                    <span key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? "bg-violet-500" : i < step ? "bg-violet-300" : "bg-slate-200"}`} />
                  ))}
                </div>
                <div className="flex gap-2">
                  {step > 0 && (
                    <button onClick={() => setStep(step - 1)} className="px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1" data-testid={`${testidRoot}-tour-prev`}>
                      <ArrowLeft className="w-3.5 h-3.5" /> Back
                    </button>
                  )}
                  {step < steps.length - 1 ? (
                    <button onClick={() => setStep(step + 1)} className="bf-pill px-5 py-2 text-sm inline-flex items-center gap-1.5" data-testid={`${testidRoot}-tour-next`}>
                      Next <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={closeModal} className="bf-pill px-5 py-2 text-sm inline-flex items-center gap-1.5" data-testid={`${testidRoot}-tour-finish`}>
                      Got it <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {panelOpen && createPortal(
        <div className="fixed inset-0 z-50 flex" data-testid={`${testidRoot}-help-panel`}>
          <div className="flex-1 bg-slate-900/30 backdrop-blur-sm" onClick={() => setPanelOpen(false)} />
          <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div>
                <div className={`text-[10px] font-mono font-bold tracking-widest ${headerTone.text}`}>{eyebrow}</div>
                <div className="font-display font-bold text-lg text-slate-900">{title}</div>
              </div>
              <button onClick={() => setPanelOpen(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600" data-testid={`${testidRoot}-help-panel-close`}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {steps.map((step, i) => {
                const t = TONE_STYLES[step.tone] || headerTone;
                const StepIconInner = step.Icon;
                return (
                  <div key={i} className="flex gap-4 items-start">
                    <div className={`w-10 h-10 rounded-xl ${t.bg} ${t.text} flex items-center justify-center shrink-0`}>
                      <StepIconInner className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-sm text-slate-900 mb-1">{step.title}</div>
                      <div className="text-xs text-slate-600 leading-relaxed">{step.body}</div>
                    </div>
                  </div>
                );
              })}
              <div className={`rounded-xl ${headerTone.bg} border ${headerTone.text} border-current/20 p-4 text-xs leading-relaxed text-slate-700`}>
                Prefer to ask a human? Tap the <b>Ask BidForge</b> chat bubble in the bottom corner — the AI help assistant answers questions about any tab, free.
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
